import { useCallback, useEffect, useRef } from "react";
import type { Location } from "history";
import { useHistory } from "react-router-dom";

interface BeforeNavigatingAwayProps {
  onBeforeNavigatingAway: () => Promise<void>;
}

export const BeforeNavigatingAway = ({
  onBeforeNavigatingAway,
}: BeforeNavigatingAwayProps) => {
  const history = useHistory();
  const isNavigatingRef = useRef(false);
  const unblockRef = useRef<(() => void) | null>(null);

  const handleNavigation = useCallback(
    // eslint-disable-next-line sonarjs/no-invariant-returns -- Disabled for plugin version update. Fix when possible
    async (location: Location) => {
      // Prevent multiple simultaneous navigation attempts
      if (isNavigatingRef.current) {
        return false;
      }

      isNavigatingRef.current = true;

      try {
        await Promise.resolve(onBeforeNavigatingAway());

        if (unblockRef.current) {
          unblockRef.current();
          unblockRef.current = null;
        }

        isNavigatingRef.current = false;

        history.push(location.pathname + (location.search || ""));
      } catch (error) {
        console.error("Error on before navigating away:", error);
      } finally {
        isNavigatingRef.current = false;
      }

      return false;
    },

    [onBeforeNavigatingAway, history],
  );

  useEffect(() => {
    unblockRef.current = history.block(location => {
      void handleNavigation(location);
      return false;
    });

    // Handle browser navigation (back/forward buttons, page refresh)
    const handleBrowserNavigationBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ""; // Chrome requires this to show the dialog
      return ""; // Some browsers use the return value
    };

    window.addEventListener(
      "beforeunload",
      handleBrowserNavigationBeforeUnload,
    );

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      window.removeEventListener(
        "beforeunload",
        handleBrowserNavigationBeforeUnload,
      );
    };
  }, [history, handleNavigation]);

  return null;
};
